/* eslint-disable react/require-default-props */
import React, {FC} from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {isEmpty, size} from 'lodash';
import {get} from 'lodash';

import FormattedNumber from '/component/base/FormattedNumber';
import Icon from '/component/base/Icon';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import ProfileSection from '/component/partial/profile/ProfileSection';
import {DownlineProfile} from '/constant/TestIDs';
import {
  ON_DEMAND_VALUE,
  ORDER_TYPE,
  PREMIER_VALUE,
  RETURN_VALUE,
} from '/enum/order.enum';
import {EVENT_NAMES, SCREEN_NAMES} from '/enum/tealium.enum';
import {withTranslation} from '/provider/TranslationProvider';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {generateCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import {styles} from './ProfileOrderHistorySection.style';

const ITEMS_LIMIT = 3;

interface Order {
  id: string;
  orderedOn: string;
  orderNumber: string;
  cv: string;
  qv: string;
  price: number;
  currencyCode: string;
  orderType: string;
}

interface ProfileOrderHistorySectionProps {
  tt?: (key: string, value: string) => string;
  orders?: Order[];
  onOrderPress?: (id: any) => void;
  onViewMore?: () => void;
  currentScreen?: string;
  lastPaidRank?: string;
}

export const ProfileOrderHistorySection: FC<
  ProfileOrderHistorySectionProps
> = ({
  tt = withTranslation.ttDefaultProp,
  orders = [],
  onOrderPress = () => {},
  onViewMore = () => {},
  currentScreen = '',
  lastPaidRank = '',
}: ProfileOrderHistorySectionProps) => {
  const visibleOrders = orders.slice(0, ITEMS_LIMIT);
  const hasMoreOrders = size(orders) > ITEMS_LIMIT;

  const featureFlag = useSelector(variablesFeatureFlag);
  const shouldShowNewOrderHistory = shouldReleaseFeature(
    get(featureFlag, 'ff_order_history', ''),
  );
  const updatePremierPlus = shouldReleaseFeature(
    get(featureFlag, 'ff_Premier_Plus', ''),
  );

  const renderOrderType = (orderType: any, orderedOn: any): string => {
    const type = `${orderType}`.trim().toLowerCase();
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const value = ORDER_TYPE[type];

    const hasPremierPlusStarted =
      updatePremierPlus &&
      getDateUtilDefaultInstance().isPremierPlusDateReached(orderedOn);

    const label = hasPremierPlusStarted ? 'Premier+' : 'Premier';

    if (value === PREMIER_VALUE) {
      return tt('orderType.premier', label);
    } else if (value === RETURN_VALUE) {
      //TODO: Determine if refactor for this is necessary as this case isn't possible to test because it's not currently used in app.
      return tt('orderType.return', 'Return');
    } else if (value === ON_DEMAND_VALUE) {
      return tt('orderType.onDemand', 'on-demand');
    }
    return '';
  };

  let renderedOrders: React.ReactNode = (
    <ListItem
      label={
        // @ts-expect-error TS(2339): Property 'text' does not exist on type '{ base: { ... Remove this comment to see the full error message
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          emphasis="low"
          style={styles.text}>
          {tt('noOrder', 'No orders yet')}
        </Text>
      }
      readonly={true}
      style={{base: styles.listItem}}
    />
  );

  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.onPageLoad,
  });
  const handleOrderPress = (id: any) => {
    sendEvent({
      currentScreenName: SCREEN_NAMES.orderDetails,
      previousScreenName: currentScreen,
      lastPaidRank: lastPaidRank,
    });
    onOrderPress(id);
  };

  if (!isEmpty(visibleOrders)) {
    renderedOrders = visibleOrders.map((order: Order, index: number) => {
      const {
        orderedOn,
        id,
        orderNumber,
        cv,
        qv,
        price = 0,
        currencyCode,
        orderType,
      } = order;
      const listItemStyle = {
        ...styles.listItem,
      };
      return (
        <ListItem
          key={index}
          label={
            <>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                style={styles.orderDateText}>
                {getDateUtilDefaultInstance().formatDate(
                  orderedOn,
                  'MMMM d, yyyy',
                )}
              </Text>
              {shouldShowNewOrderHistory ? (
                <Text
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  style={styles.orderNumberText}>
                  #{orderNumber}
                </Text>
              ) : null}
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                variant="caption"
                emphasis="low"
                style={styles.orderMetaText}
                testID={DownlineProfile.commissionableVolumeTotal}>
                {tt('list.cv', 'CV')} {parseInt(cv, 10)}
                {' · '}
                {tt('list.qv', 'QV')} {parseInt(qv, 10)}
                {' · '}
                {renderOrderType(orderType, orderedOn)}
              </Text>
            </>
          }
          suffix={
            <View style={styles.orderHistoryList}>
              <FormattedNumber
                value={price}
                currencyCode={currencyCode}
                decimalPlaces={2}
              />
              <Icon name="chevron-right" style={styles.arrowIcon} />
            </View>
          }
          onPress={generateCallback({
            func: handleOrderPress,
            params: {orderId: id},
          })}
          style={{base: listItemStyle}}
        />
      );
    });
  }

  return (
    <ProfileSection
      analyticsTitle="Order History"
      style={{base: styles.base}}
      title={tt('heading', 'Order History')}>
      {renderedOrders}
      {hasMoreOrders && (
        <View
          style={styles.viewMoreOrders}
          {...testProperties(DownlineProfile.viewMoreButton)}>
          <TouchableOpacity
            style={styles.viewMoreOrderButton}
            onPress={generateCallback({func: onViewMore})}>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              useCompetency={true}
              variant="button">
              {tt('showAllOrderHistory', 'Show all order history')}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </ProfileSection>
  );
};

export default ProfileOrderHistorySection;
